import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { TArrayTypes } from '../../Declarations';
import Delimeters from './Delimeters';
import { flattenJSON, safeStringify, useClipboard } from '../../Common';
import { GlobalConstants } from '../../GlobalConstants';
import { Check, Copy } from 'lucide-react';

export interface IDelimetersData {
  arrayStyle: string;
  delimiter: string;
  onHandleFlatten: () => void;
  onSetDelimiter: (delimeter: string) => void;
  onSetArrayStyle: (style: TArrayTypes) => void;
}

const Playground = (styles: React.CSSProperties | undefined): ReactElement => {
  const [inputText, setInputText] = useState<string>(
    safeStringify(GlobalConstants.sampleJSON)
  );
  const [outputText, setOutputText] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [delimiter, setDelimiter] = useState<string>('.');
  const [arrayStyle, setArrayStyle] = useState<TArrayTypes>('bracket');

  const { copied, copy } = useClipboard(outputText);

  const parsedInput = useMemo(() => {
    try {
      setError('');
      return JSON.parse(inputText || 'null');
    } catch (e: unknown) {
      if (e instanceof Error) {
        setError(e.message ?? 'Invalid JSON');
      } else {
        setError(String(e));
      }
      return null;
    }
  }, [inputText]);

  function handleFlatten(): void {
    try {
      const flat = flattenJSON(parsedInput, { delimiter, arrayStyle });
      setOutputText(safeStringify(flat));
    } catch (e: unknown) {
      if (e instanceof Error) {
        setError(e.message ?? 'Failed to flatten JSON');
      } else {
        setError(String(e));
      }
    }
  }

  useEffect(() => {
    // Auto-flatten on load
    handleFlatten();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delimiter, arrayStyle]);

  return (
    <section id="playground" className="py-2 scroll-mt-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-6 max-w-2xl">
          <h2 className="text-3xl font-bold tracking-tight">Paste JSON on the left</h2>
          <p className="mt-2 text-slate-600">collect flattened output on the right</p>
        </div>

        <Delimeters
          arrayStyle={arrayStyle}
          delimiter={delimiter}
          onHandleFlatten={handleFlatten}
          onSetDelimiter={setDelimiter}
          onSetArrayStyle={setArrayStyle}
        />

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Before */}
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center justify-between p-3 border-b">
              <div className="text-sm font-semibold text-slate-700">Before (JSON)</div>
              {error ? (
                <div className="text-xs text-red-600">{error}</div>
              ) : (
                <div className="text-xs text-slate-500">Valid JSON</div>
              )}
            </div>
            <textarea
              className="w-full resize-none rounded-2xl p-4 font-mono text-sm focus:outline-none"
              value={inputText}
              onChange={e => setInputText(e.target.value)}
              spellCheck={false}
              placeholder="Sinta"
              style={styles}
            />
            <div className="flex gap-2 p-3 border-t">
              <button
                className="rounded-lg border px-3 py-1 text-sm"
                onClick={() => setInputText(safeStringify(GlobalConstants.sampleJSON))}>
                Load sample
              </button>
              <button
                className="rounded-lg border px-3 py-1 text-sm"
                onClick={() => {
                  try {
                    setInputText(JSON.stringify(JSON.parse(inputText), null, 2));
                  } catch (err) {
                    console.error(err);
                  }
                }}>
                Prettify
              </button>
              <button
                className="rounded-lg border px-3 py-1 text-sm"
                onClick={() => {
                  try {
                    setInputText(JSON.stringify(JSON.parse(inputText)));
                  } catch (err) {
                    console.error(err);
                  }
                }}>
                Minify
              </button>
            </div>
          </div>

          {/* After */}
          <div className="rounded-2xl border border-amber-200 bg-amber-50/50 shadow-sm">
            <div className="flex items-center justify-between p-3 border-b border-amber-200/70">
              <div className="text-sm font-semibold text-slate-700">After (Flattened)</div>
              <button
                onClick={copy}
                className="inline-flex items-center gap-2 rounded-lg border border-amber-300 bg-white px-3 py-1 text-sm">
                {copied ? <Check className="h-4 w-4"/> : <Copy className="h-4 w-4"/>}
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
            <textarea
              readOnly
              className="w-full resize-none rounded-2xl p-4 font-mono text-sm bg-transparent focus:outline-none"
              value={outputText}
              placeholder="INV-1001"
              style={styles}
            />
            <div className="flex gap-2 p-3 border-t border-amber-200/70">
              <button
                className="rounded-lg border border-amber-300 px-3 py-1 text-sm bg-white"
                onClick={() => {
                  try {
                    setOutputText(JSON.stringify(JSON.parse(outputText), null, 2));
                  } catch (err) {
                    console.error(err);
                  }
                }}>
                Prettify
              </button>
              <button
                className="rounded-lg border border-amber-300 px-3 py-1 text-sm bg-white"
                onClick={() => {
                  try {
                    setOutputText(JSON.stringify(JSON.parse(outputText)));
                  } catch (err) {
                    console.error(err);
                  }
                }}>
                Minify
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Playground;
